import { useEffect, useRef } from 'react'
import { Chart, registerables, type ChartConfiguration } from 'chart.js'
import { palette } from '@versports/theme'
import { baseChartOptions, withAlpha } from '../../components/chartTheme'
import { toIndex100, type CycleView } from './signalsModel'

Chart.register(...registerables)

/**
 * Índice por ciclo dentro de una sesión (barras) + frecuencia cardíaca en
 * bpm sobre el eje derecho: pico durante el estímulo y valor al final de la
 * ventana de recuperación. Las dos cosas juntas porque el índice se lee
 * mirando de dónde bajó el corazón y hasta dónde llegó — separarlas obliga a
 * cruzar dos gráficos a mano.
 */
export function SignalsCycleChart({ cycles }: { cycles: CycleView[] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    const labels = cycles.map((c) => c.label)
    const config: ChartConfiguration<'bar' | 'line'> = {
      // Mixto: el `type` de cada dataset manda, pero Chart.js igual exige uno
      // a nivel config — `bar` es el de las barras del índice.
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            type: 'bar',
            label: 'Índice cardíaco',
            data: cycles.map((c) => toIndex100(c.cycle.r_cardio)),
            backgroundColor: withAlpha(palette.primary, 0.75),
            borderColor: palette.primary,
            borderWidth: 1,
            borderRadius: 4,
            yAxisID: 'yIndex',
          },
          {
            type: 'bar',
            label: 'Índice cardíaco + piel',
            data: cycles.map((c) => toIndex100(c.cycle.r_cardio_eda)),
            backgroundColor: withAlpha(palette.secondary, 0.55),
            borderColor: palette.secondary,
            borderWidth: 1,
            borderRadius: 4,
            yAxisID: 'yIndex',
          },
          {
            type: 'line',
            label: 'HR pico (bpm)',
            data: cycles.map((c) => c.hrPeak),
            // Ámbar, no rojo: el rojo del theme queda demasiado cerca del
            // naranja de las barras del índice y las dos series se confunden.
            borderColor: palette.warning,
            backgroundColor: palette.warning,
            tension: 0.25,
            spanGaps: true,
            yAxisID: 'yHr',
          },
          {
            type: 'line',
            label: 'HR fin recuperación (bpm)',
            data: cycles.map((c) => c.hrRecoveryEnd),
            borderColor: palette.goal,
            backgroundColor: palette.goal,
            borderDash: [5, 4],
            tension: 0.25,
            spanGaps: true,
            yAxisID: 'yHr',
          },
        ],
      },
      options: {
        ...baseChartOptions(),
        scales: {
          x: {
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          yIndex: {
            type: 'linear',
            position: 'left',
            min: 0,
            max: 100,
            title: { display: true, text: 'Índice 0–100', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          yHr: {
            type: 'linear',
            position: 'right',
            title: { display: true, text: 'bpm', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { display: false },
          },
        },
      } as ChartConfiguration<'bar' | 'line'>['options'],
    }

    chartRef.current = new Chart(canvasRef.current, config)
    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [cycles])

  return (
    <div style={{ height: 280 }}>
      <canvas ref={canvasRef} />
    </div>
  )
}
